<template>
  <div :class="mobileUiConfig.mainClass">
    <div v-if="mobileUiConfig.showTitle" class="b-movie-reminder__icon">
      <i class="icon-alarm"></i>
    </div>
    <div v-if="mobileUiConfig.showTitle" class="b-movie-reminder__txt-wrap">
      <h3 class="b-movie-reminder__title">{{ dictionary.title }}</h3>
      <div class="b-movie-reminder__text">
        <p>{{ dictionary.text }}</p>
      </div>
    </div>
    <div
      v-if="mobileUiConfig.showMobileTitle"
      class="b-movie-reminder__header-wrapper">
      <div>
        <div class="b-movie-reminder__title--mobile">
          {{ selectedMovie.title }}
        </div>
        <div class="b-movie-reminder__text--mobile">
          {{ selectedMovie.subTitle }}
        </div>
      </div>
      <div class="b-movie-reminder__poster">
        <img :src="selectedMovie.img" :alt="selectedMovie.title" />
      </div>
    </div>
    <div v-if="currentPage === 'home'" class="b-movie-reminder__screen-home">
      <div class="b-movie-reminder__container">
        <div class="b-movie-reminder__wrapper">
          <div class="b-movie-reminder__days">
            <div class="b-movie-reminder__counter">
              {{ renderedTime.days }}
            </div>
            <span>{{ dictionary.days }}</span>
          </div>
          <div class="b-movie-reminder__hours">
            <div class="b-movie-reminder__counter">
              {{ renderedTime.hours }}
            </div>
            <span>{{ dictionary.hours }}</span>
          </div>
          <div class="b-movie-reminder__minutes">
            <div class="b-movie-reminder__counter">
              {{ renderedTime.minutes }}
            </div>
            <span>{{ dictionary.minutes }}</span>
          </div>
        </div>
        <button
          type="button"
          class="b-movie-reminder__cta-notify"
          @click="goToForm">
          {{ dictionary.notify }}
        </button>
      </div>
    </div>
    <div v-if="currentPage === 'form'" class="b-movie-reminder__screen-form">
      <Form v-slot="{ meta, errors }" @submit="submitData">
        <Loader v-if="isLoading"></Loader>
        <Alert
          v-if="!isLoading && errorMsgs.length"
          key="submit-errors"
          dismiss-by-native=""
          type="danger"
          :messages="errorMsgs"
          @close="removeErrors"></Alert>
        <div class="form-floating b-movie-reminder__input-holder">
          <Field
            id="email"
            class="form-control"
            :class="{
              'form-control--error': errors.email && errors.email.length,
            }"
            name="email"
            type="email"
            placeholder="Email"
            rules="required|email"
            :validate-on-input="emailMeta.touched"
            @blur="emailBlur" />
          <label for="email">{{ dictionary.email }}</label>
          <ErrorMessage v-slot="{ message }" name="email">
            <div class="field-validation-error">
              {{ message }}
            </div>
          </ErrorMessage>
        </div>

        <div class="form-floating b-movie-reminder__input-holder">
          <div class="custom-chck">
            <Field
              id="userTerms"
              name="userTerms"
              type="checkbox"
              value="true"
              rules="required"
              :class="{
                'form-control--error':
                  errors.userTerms && errors.userTerms.length,
              }" />

            <label for="userTerms">
              <span class="custom-chck__span">
                <div class="custom-chck__text">
                  <em>{{ dictionary.acceptTerms }}</em>
                  <div v-html="dictionary.acceptNotice"></div>
                </div>
              </span>
            </label>
            <ErrorMessage v-slot="{ message }" name="userTerms">
              <div class="field-validation-error">
                {{ message }}
              </div>
            </ErrorMessage>
          </div>
        </div>
        <nav class="b-movie-reminder__form-nav-wrapper">
          <button
            class="b-movie-reminder__cancel"
            type="button"
            @click="cancelReminder">
            {{ dictionary.cancel }}
          </button>
          <button
            class="b-movie-reminder__submit btn--block"
            type="submit"
            :disabled="!meta.valid">
            <span>
              {{ dictionary.submit }}
            </span>
          </button>
        </nav>
      </Form>
    </div>
    <div v-if="currentPage === 'submitted'">
      <div class="b-movie-reminder__submitted-container">
        <i class="icon-check b-movie-reminder__submitted-icon"></i>
        <div class="b-movie-reminder__submitted-title">
          {{ dictionary.submitted }}
        </div>
        <div class="b-movie-reminder__submitted-desc">
          {{ dictionary.submittedDesc }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { checkCaptcha, getLocale } from '../../lib/lib'
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import Alert from './../components/Shared/Alert.vue'
import Loader from './../components/Shared/Loader.vue'
import {
  ErrorMessage,
  Field,
  Form,
  configure,
  defineRule,
  useField,
} from 'vee-validate'
import { email, required } from '@vee-validate/rules'
import { localize } from '@vee-validate/i18n'
import postRequestHandler from '../../lib/postRequestHandler'
import getErrorMessage from '../../lib/getErrorMessage'
import { normalizeToAthens } from '../../lib/date'

defineRule('required', required)
defineRule('email', email)

const { handleBlur: emailBlur, meta: emailMeta } = useField(
  'email',
  'required|email',
)

const props = defineProps({
  dictionary: {
    default() {
      return {}
    },
    type: Object,
  },
  configuration: {
    default() {
      return {}
    },
    type: Object,
  },
  start: { default: '', type: String },
  due: {
    default: '',
    type: String,
  },
  movieId: { default: '', type: String },
  selectedMovie: {
    default() {
      return {}
    },
    type: Object,
  },
  mobileUiConfig: {
    default() {
      return {
        mainClass: 'b-movie-reminder',
        showTitle: true,
        showMobileTitle: false,
      }
    },
    type: Object,
  },
})

let remainingTime = 0
const errorMsgs = ref([])
const isLoading = ref(true)
const renderedTime = reactive({
  days: '--',
  hours: '--',
  minutes: '--',
  seconds: '--',
})
let countdownTimer = 0

const startDate = normalizeToAthens(props.start)
const dueDate = normalizeToAthens(props.due)

if (
  startDate instanceof Date &&
  !isNaN(startDate.getTime()) &&
  dueDate instanceof Date &&
  !isNaN(dueDate.getTime())
) {
  remainingTime = dueDate.getTime() - startDate.getTime()
}

let seconds = remainingTime / 1000

const countDown = () => {
  timer()

  countdownTimer = setInterval(() => {
    timer()
  }, 1000)
}

function pad(n) {
  return n < 10 ? `0${n}` : String(n)
}

const timer = () => {
  const days = Math.floor(seconds / 24 / 60 / 60)
  const hoursLeft = Math.floor(seconds - days * 86400)
  const hours = Math.floor(hoursLeft / 3600)
  const minutesLeft = Math.floor(hoursLeft - hours * 3600)
  const minutes = Math.floor(minutesLeft / 60)
  const remainingSeconds = seconds % 60

  renderedTime['days'] = pad(days)
  renderedTime['hours'] = pad(hours)
  renderedTime['minutes'] = pad(minutes)
  renderedTime['seconds'] = pad(remainingSeconds)

  if (seconds === 0) {
    clearInterval(countdownTimer)
  } else {
    seconds -= 1
  }
}

onMounted(() => {
  checkCaptcha(() => (isLoading.value = false))

  if (movieReminderSubmitted()) {
    goToSubmitted()
  }

  if (remainingTime) {
    countDown()
  }

  configure({
    generateMessage: localize(getLocale(), {
      fields: {
        email: {
          required: props.dictionary.emailRequired,
          email: props.dictionary.emailRegex,
        },
        userTerms: {
          required: props.dictionary.userTermsRequired,
        },
      },
    }),
  })
})

onBeforeUnmount(() => {
  clearInterval(countdownTimer)
})

// navigation functionality

const currentPage = ref('home')

const goToForm = () => {
  currentPage.value = 'form'
}

const goToHome = () => {
  currentPage.value = 'home'
}

const goToSubmitted = () => {
  currentPage.value = 'submitted'
}

// cache submission
const cacheMovieReminder = () => {
  sessionStorage.setItem(props.movieId, true)
}
const movieReminderSubmitted = () => {
  return sessionStorage.getItem(props.movieId)
}
const removeCachedMovieReminder = () => {
  sessionStorage.removeItem(props.movieId)
}

// form functionality

const submitHandler = props.configuration.submit

const submitData = async (values) => {
  if (isLoading.value) {
    return false
  }

  removeErrors()
  isLoading.value = true

  const postData = {
    movieId: props.movieId,
    email: values.email,
  }

  await postRequestHandler({
    postData,
    endpoint: submitHandler,
    successCallback: handleSuccess,
    errorCallback: handleError,
    refreshTokenHandler: props.configuration.refresh,
    captchaAction: props.configuration.captchaAction,
  })
}
const removeErrors = () => {
  isLoading.value = false

  errorMsgs.value = []
}
const handleError = (messages) => {
  isLoading.value = false

  errorMsgs.value = getErrorMessage(
    messages,
    props.dictionary.defaultErrorMessage,
  )
}
const handleSuccess = () => {
  cacheMovieReminder()
  goToSubmitted()
}

const cancelReminder = () => {
  removeCachedMovieReminder()
  goToHome()
}
</script>
